import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Form, Row, Col, Button } from "react-bootstrap";
import DialogFooter from "./DialogFooter.js";
import { MultiSelect } from "primereact/multiselect";
import LoadingOverlay from "react-loading-overlay-ts";
import { fetchUserAttributes } from "../../../apis/dashboardApi.js";
import { buildDashboardModule, buildDashboardPayload } from "./dashboardPayloadConstants.js";

const CreateMaterialView = ({
  initialSettings,
  onHide,
  onSettingsSave,
  isUpdateMode = false,
  toastRef,
  reloadDashboard,
}) => {
  // Redux user info
  const { userInformation } = useSelector((state) => state.UserReducer);
  // Loading state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // IL and Category options
  const [ilOptions, setIlOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Helper to parse comma-separated string to array of objects for MultiSelect
  const parseMultiSelect = (value, options, separator = "$%") => {
    if (!value || !options.length) return [];
    const codes = value.split(separator).map((s) => s.trim()).filter(Boolean);
    return options.filter((opt) => codes.includes(opt.code));
  };

  // Project types logic (From)
  const projectTypes = ["My Projects", "All Projects"];
  const initialProjectTypes = initialSettings?.From
    ? initialSettings.From.split(",").map((s) => s.trim())
    : [];
  const [selectedProjectTypes, setSelectedProjectTypes] = useState(initialProjectTypes);

  // Selected IL and Category
  const [selectedIL, setSelectedIL] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);

  // Fetch IL and Category options from API on mount
  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const userId = userInformation?.userid;
        const data = await fetchUserAttributes(userId);
        const result = Array.isArray(data.UserAttributes)
          ? data.UserAttributes
          : Array.isArray(data)
            ? data
            : [];
        if (result.length > 0) {
          // Unique ILs
          const uniqueILs = Array.from(new Set(result.map((item) => item.IL).filter(Boolean)));
          // setIlOptions(uniqueILs.map((il) => ({ name: il, code: il })));
          setIlOptions([
            { name: "No Value", code: "#" },
            ...uniqueILs.map((il) => ({ name: il, code: il })),
          ]);
          // Unique Categories
          const uniqueCategories = Array.from(
            new Set(result.map((item) => item.Category).filter(Boolean)),
          );
          setCategoryOptions(uniqueCategories.map((cat) => ({ name: cat, code: cat })));
        }
      } catch (error) {
        console.error("Error fetching IL/Category options:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInformation?.userid]);

  // When options are loaded, initialize selectedIL/selectedCategory from initialSettings if present
  useEffect(() => {
    if (initialSettings?.IL && ilOptions.length > 0 && selectedIL.length === 0) {
      setSelectedIL(parseMultiSelect(initialSettings.IL, ilOptions, "$%"));
    }
    if (initialSettings?.Category && categoryOptions.length > 0 && selectedCategory.length === 0) {
      setSelectedCategory(parseMultiSelect(initialSettings.Category, categoryOptions, ","));
    }
  }, [ilOptions, categoryOptions]);

  // Utility to build payload for create/update
  const buildMaterialViewPayload = () => {
    const userId = userInformation?.userid;
    const module = buildDashboardModule({
      moduleId: isUpdateMode ? initialSettings?.Module_ID || "" : "",
      moduleName: isUpdateMode ? initialSettings?.Title : `MaterialView_1`,
      moduleType: "Materials",
      userId,
      config: {
        IL: selectedIL.length > 0 ? selectedIL.map((il) => il.code).join("$%") : "",
        Category:
          selectedCategory.length > 0 ? selectedCategory.map((cat) => cat.code).join(",") : "",
        From: selectedProjectTypes.join(","),
      },
      Sequence: isUpdateMode ? initialSettings?.Sequence : -1
    });
    return buildDashboardPayload({ module });
  };

  // Create or update handler
  const handleCreateOrUpdate = async () => {
    setSubmitting(true);
    const payload = buildMaterialViewPayload();
    try {
      const { createOrUpdateDashboardModuleView } = await import("../../../apis/dashboardApi.js");
      await createOrUpdateDashboardModuleView(payload);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: isUpdateMode
            ? "Material view updated successfully"
            : "Material view created successfully",
          life: 3000,
        });
      }
      if (typeof reloadDashboard === "function") {
        reloadDashboard();
      }
      if (onSettingsSave) {
        onSettingsSave(payload);
      }
      onHide();
    } catch (error) {
      console.error("Error creating/updating material view:", error);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to ${isUpdateMode ? "update" : "create"} material view.`,
          life: 5000,
        });
      }
      setSubmitting(false);
    }
  };

  const isFormValid =
    selectedProjectTypes.length > 0 && selectedIL.length > 0 && selectedCategory.length > 0;

  return (
    <LoadingOverlay active={loading} spinner text="Loading user attributes...">
      {!loading && (
        <>
          <Row className="project-form">
            <Col md={4}>
              <Form.Group className="d-flex align-items-center gap-3">
                <Form.Label className="mb-0 module-form-label" style={{ whiteSpace: "nowrap" }}>
                  From <span className="mandatory-asterisk"> *</span>
                </Form.Label>
                <div className="d-flex flex-column">
                  {projectTypes.map((type) => (
                    <Form.Check
                      key={type}
                      type="checkbox"
                      id={type.replace(/\s/g, "")}
                      name={type}
                      label={type}
                      checked={selectedProjectTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProjectTypes((prev) => [...prev, type]);
                        } else {
                          setSelectedProjectTypes((prev) => prev.filter((t) => t !== type));
                        }
                      }}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 module-form-label" style={{ whiteSpace: "nowrap" }}>
                  IL <span className="mandatory-asterisk"> *</span>
                </Form.Label>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <MultiSelect
                    value={selectedIL}
                    onChange={(e) => setSelectedIL(e.value)}
                    options={ilOptions}
                    optionLabel="name"
                    placeholder="Select IL"
                    maxSelectedLabels={3}
                    className="w-full"
                    filter
                  />
                </div>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 module-form-label" style={{ whiteSpace: "nowrap" }}>
                  Category <span className="mandatory-asterisk"> *</span>
                </Form.Label>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <MultiSelect
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.value)}
                    options={categoryOptions}
                    optionLabel="name"
                    placeholder="Select Category"
                    maxSelectedLabels={3}
                    className="w-full"
                    filter
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <DialogFooter
            submitting={submitting}
            isFormValid={isFormValid}
            isUpdateMode={isUpdateMode}
            onCancel={onHide}
            onSubmit={handleCreateOrUpdate}
          />
        </>
      )}
    </LoadingOverlay>
  );
};

export default CreateMaterialView;
